import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// sent OTP
export const sentOTP = async (countryCode, phoneNumber) => { 
  try {
    const response = await api.post("/sourcing/login/send_otp", {
      "country_code": countryCode,
      "phone": phoneNumber,
    });
  } catch (error) { 
    console.error("Error in sending OTP:", error);
    throw error;
  }
}

// verify OTP
export const verifyOTP = async (countryCode, phoneNumber, otp, position) => { 
  try {
    const response = await api.post("/sourcing/login/verify_otp", {
      "country_code": countryCode,
      "phone": phoneNumber,
      "otp": otp,
      "location": position
    });
    const { access_token } = response.data;
    if (access_token) {
      localStorage.setItem("token", access_token);
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Immediately fetch user data and permissions after successful OTP verification
      try {
        // Get user data first
        const userData = await getCurrentUser();
        
        // Get user ID from token
        const decodedToken = jwtDecode(access_token);
        const userId = decodedToken.sub || decodedToken.user_id || decodedToken.id || userData.id;
        
        if (!userId) {
          throw new Error('Could not extract user ID from token or user data');
        }
        
        // Fetch permissions
        const permissions = await getUserPermissions(userId);
        
        // Return all the necessary data
        return {
          ...response.data,
          userData,
          permissions
        };
      } catch (error) {
        console.error('Error fetching user data or permissions:', error);
        throw error;
      }
    }
    return response.data;
  } catch (error) { 
    console.error("Error in verifying OTP:", error);
    throw error;
  }
}

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('permissionsTimestamp');
  delete api.defaults.headers.common['Authorization'];
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get("/users/me");
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

// Fetch user permissions with caching
export const getUserPermissions = async (userId) => {
  try {
    // Check if permissions exist in localStorage and are not expired
    const cachedPermissions = localStorage.getItem('userPermissions');
    const permissionsTimestamp = localStorage.getItem('permissionsTimestamp');

    if (cachedPermissions && permissionsTimestamp) {
      const now = new Date().getTime();
      if (now - parseInt(permissionsTimestamp) < PERMISSIONS_TTL) {
        return JSON.parse(cachedPermissions);
      }
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found in localStorage');
      throw new Error('Authentication required');
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    const response = await api.get(`/rbac/users/${userId}/permissions`, { headers });
    
    if (!response.data || !response.data.resources) {
      throw new Error('Invalid permissions format received from server');
    }

    // Cache the permissions and timestamp
    localStorage.setItem('userPermissions', JSON.stringify(response.data));
    localStorage.setItem('permissionsTimestamp', new Date().getTime().toString());
    
    return response.data;
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    if (error.response) {
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
    }
    throw error;
  }
};

// Initialize auth header if token exists 
const token = localStorage.getItem('token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Get all services (for lead form)
export const getServices = async () => {
  try {
    const response = await api.get("/services");
    return response.data;
  } catch (error) {
    console.error('Error in getting services', error);
    throw error;
  }
};

// Create lead form
export const createLeadForm = async (leadFormData) => {
  try {
    const response = await api.post("/sourcing_leads", leadFormData);
    return response.data;
  } catch (error) {
    console.error('Error in creating Leads', error);
    throw error;
  }
};


// Get lead form options
export const getFormOptions = async () => {
  try {
    const response = await api.get("/sourcing_leads/form_options");
    return response.data;
  } catch (error) {
    console.error('Error in fetching form options', error);
    throw error;
  }
};

// Get hoods by cluster id
export const getHoodsByCluster = async (clusterId) => {
  try {
    const response = await api.get(`/geos/hoods/by_cluster/${clusterId}`);
    return response.data;
  } catch (error) {
    console.error('Error in fetching hoods', error);
    throw error;
  }
};

// Get training centers by service id (only TCs with upcoming batches)
export const getTrainingCentersByService = async (serviceId) => {
  try {
    const response = await api.get(`/training/batches/training_centers?service_id=${serviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error in fetching training centers by service', error);
    throw error;
  }
};

// Get upcoming batches for a TC + service
export const getUpcomingBatches = async (trainingCenterId, serviceId) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const response = await api.get(`/training/batches`, {
      params: { training_center_id: trainingCenterId, service_id: serviceId, from_date: today }
    });
    return response.data;
  } catch (error) {
    console.error('Error in fetching upcoming batches', error);
    throw error;
  }
};

// Get training centers
export const getTrainingCenters = async () => {
  try {
    const response = await api.get("/geos/training_centers");
    return response.data;
  } catch (error) {
    console.error('Error in fetching training centers', error);
    throw error;
  }
};

// Get all geos
export const getGeosList = async () => {
  const selectedRegion = localStorage.getItem('selectedRegion') || "1";
  try {
    const response = await api.get(`/geos/regions/${selectedRegion}`);
    return response.data;
  } catch (error) {
    console.error('Error in getting geos', error);
    throw error;
  }
}

// Helper function to check if user has admin access
const hasAdminAccess = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    const decodedToken = jwtDecode(token);
    const userId = decodedToken.sub || decodedToken.user_id || decodedToken.id;
    if (!userId) return false;
    
    const permissions = await getUserPermissions(userId);
    return hasPermission(permissions, 'sourcing', 'acess_control');
  } catch (error) {
    console.error('Error checking admin access:', error);
    return false;
  }
};

// Get my leads
export const myLeads = async (
  limit,
  offset,
  from_date = null,
  to_date = null,
  phone = null,
  status = null,
  region_ids = null
) => {
  try {
    const params = {
      limit,
      offset,
      ...(from_date && { from_date }),
      ...(to_date && { to_date }),
      ...(phone && { phone }),
      ...(status && { status }),
    };
    
    const headers = {};
    if (region_ids) {
      headers["region-ids"] = region_ids;
    }

    const response = await api.get("/sourcing_leads/me", { params, headers });
    return response.data;
  } catch (error) {
    console.error("Error in getting leads", error);
    throw error;
  }
};

// Get all leads (admin only)
export const getAllLeads = async (
  limit,
  offset,
  from_date = null,
  to_date = null,
  phone = null,
  status = null,
  region_ids = null
) => {
  try {
    const params = {
      limit,
      offset,
      ...(from_date && { from_date }),
      ...(to_date && { to_date }),
      ...(phone && { phone }),
      ...(status && { status }),
    };
    
    const headers = {};
    if (region_ids) {
      headers["region-ids"] = region_ids;
    }

    const response = await api.get("/sourcing_leads", { params, headers });
    return response.data;
  } catch (error) {
    console.error("Error in getting all leads", error);
    throw error;
  }
};

// Get my potential leads
export const potentialLeads = async(limit, offset, status=null, from_date=null, to_date=null) => {
  try {
    const selectedRegion = localStorage.getItem('selectedRegion') || "1";
    const params = {
      limit,
      offset,
      region_id: selectedRegion,
      ...(status && { status }),
      ...(from_date && { from_date }),
      ...(to_date && { to_date })
    };
    
    const response = await api.get("/sourcing_leads/me/potential_lead", { params });
    return response.data;
  } catch (error) {
    console.error('Error in getting potential leads', error);
    throw error;
  }
}

// Get all potential leads (admin only)
export const getAllPotentialLeads = async(limit, offset, status=null, from_date=null, to_date=null) => {
  try {
    const selectedRegion = localStorage.getItem('selectedRegion') || "1";
    const params = {
      limit,
      offset,
      region_id: selectedRegion,
      ...(status && { status }),
      ...(from_date && { from_date }),
      ...(to_date && { to_date })
    };
    
    const response = await api.get("/sourcing_leads/potential_lead", { params });
    return response.data;
  } catch (error) {
    console.error('Error in getting all potential leads', error);
    throw error;
  }
}

// reject potential lead 
export const rejectPotentialLead = async (
  potential_lead_id,
  status_remarks,
  status
) => {
  try {
    const response = await api.put(
      `/sourcing_leads/potential_lead/${potential_lead_id}`,
      {
        status: status,
        status_remarks: status_remarks
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error in rejecting potential leads", error);
    throw error;
  }
};


export const automaticCall = async (phoneNumber) => {
  try {
    const response = await api.post("/users/initiate_call", {
      country_code: "+91",
      phone: phoneNumber,
    });
    if (response.status !== 200) {
      throw new Error(
        `Failed to initiate call. Status code: ${response.status}`
      );
    }
  } catch (error) {
    console.error("Error in calling", error);
    throw error;
  }
};

// Error handling interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          // Handle unauthorized - clear token and redirect to login
          logout();
          window.location.href = '/login';
          break;
        case 403:
          // Handle forbidden
          console.error('Access forbidden');
          break;
          // console.error('Access forbidden');
        case 404:
          // Handle not found
          console.error('Resource not found');
          break;
        default:
          // Handle other errors
          console.error('An error occurred:', error.response.data);
          break;
      }
    }
    return Promise.reject(error);
  }
);

// Update lead
export const updateLead = async (leadId, updateData) => {
  try {
    const response = await api.put(`/sourcing_leads/me/${leadId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error in updating lead', error);
    throw error;
  }
};

// Agent Management APIs
export const createAgent = async (agentData) => {
  try {
    console.log('Making API call to:', `${API_BASE_URL}/sourcing`);
    console.log('With data:', agentData);
    const regionIdsAsStrings = agentData.regionIds.map((id) => id.toString());
    const response = await api.post("/sourcing", {
      user: {
        name: agentData.name,
        phone: agentData.phoneNumber,
        email: agentData.personalEmail,
        country_code: "+91",
      },
      role_group_id: parseInt(agentData.role),
      region_ids: regionIdsAsStrings,
      date_of_joining: agentData.doj,
    });
    return response.data;
  } catch (error) {
    console.error('Error in creating agent:', error);
    throw error;
  }
};

export const updateAgent = async (agentId, agentData) => {
  try {
    const response = await api.put(`/sourcing/agents/${agentId}`, {
      user: {
        name: agentData.name,
        phone: agentData.phoneNumber,
        email: agentData.personalEmail,
        country_code: "+91"
      },
      role_group_id: parseInt(agentData.role),
      region_ids: [agentData.region.toString()],
      date_of_joining: agentData.doj
    });
    return response.data;
  } catch (error) {
    console.error('Error in updating agent:', error);
    throw error;
  }
};

export const deleteAgent = async (agentId) => {
  try {
    await api.delete(`/sourcing/agents/${agentId}`);
  } catch (error) {
    console.error('Error in deleting agent:', error);
    throw error;
  }
};

export const getAgents = async (page = 1, size = 50) => {
  try {
    const response = await api.get(`/sourcing`, {
      params: {
        page,
        size
      }
    });
    console.log('Raw API Response:', response);
    console.log('Response data:', response.data);
    
    // Return both items array and total count
    return {
      items: response.data?.items || [],
      total: response.data?.total || 0
    };
  } catch (error) {
    console.error('Error in fetching agents:', error);
    throw error;
  }
};

export const getRegions = async () => {
  try {
    const response = await api.get("/geos/regions");
    console.log('Raw regions response:', response.data);
    
    // Transform the response to extract regions with their IDs and names
    const regions = [];
    const regionData = response.data;
    
    // Add debug logs
    console.log('Region data type:', typeof regionData);
    console.log('Region data structure:', {
      hasName: 'name' in regionData,
      hasClusters: 'clusters' in regionData,
      clustersType: regionData.clusters ? typeof regionData.clusters : 'undefined'
    });

    // Handle both array and single object responses
    if (Array.isArray(regionData)) {
      // If it's an array of regions
      regionData.forEach(region => {
        if (region.clusters && Array.isArray(region.clusters)) {
          region.clusters.forEach(cluster => {
            regions.push({
              id: cluster.id,
              name: `${region.name}: ${cluster.name}`
            });
          });
        }
      });
    } else if (regionData && typeof regionData === 'object') {
      // If it's a single region object
      if (regionData.clusters && Array.isArray(regionData.clusters)) {
        regionData.clusters.forEach(cluster => {
          regions.push({
            id: cluster.id,
            name: `${regionData.name}: ${cluster.name}`
          });
        });
      }
    }

    console.log('Transformed regions:', regions);
    return regions;
  } catch (error) {
    console.error('Error in fetching regions:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data
    });
    throw error;
  }
};

// Get minimal regions for dropdowns
export const getRegionsMinimal = async () => {
  try {
    const response = await api.get("/geos/regions/minimal");
    return response.data;
  } catch (error) {
    console.error('Error in fetching minimal regions:', error);
    throw error;
  }
};

export const getRoles = async () => {
  // Hardcoded roles as specified
  const roles = [
    { id: 15, name: "Sourcing Telecaller" },
    { id: 16, name: "Sourcing Field Recruiter" },
    { id: 17, name: "Sourcing Registration Expert" },
    { id: 18, name: "Sourcing Admin" },
    { id: 19, name: "Sourcing Cluster Manager" },
    { id: 21, name: "Sourcing Agency" }
  ];
  return roles;
};


// Helper functions to check permissions
export const hasPermission = (permissions, resource, action) => {
  if (!permissions?.resources?.[resource]?.actions?.[action]) {
    return false;
  }
  return true;
};

export const getPermittedRegions = (permissions, resource, action) => {
  if (!permissions?.resources?.[resource]?.actions?.[action]?.attributes?.region_ids) {
    return [];
  }
  return permissions.resources[resource].actions[action].attributes.region_ids;
};

export const getSourcingStatistics = async (from_date = null, to_date = null, source = null, role_group_id = null, cluster_id = null) => {
  try {
    const selectedRegion = localStorage.getItem('selectedRegion') || "1";
    const params = {
      from_date,
      to_date,
      source,
      region_id: selectedRegion,
      role_group_id: role_group_id ? parseInt(role_group_id) : null,
      cluster_id: cluster_id ? parseInt(cluster_id) : null
    };

    // Remove null values from params
    Object.keys(params).forEach(key => params[key] === null && delete params[key]);

    console.log('Sourcing Statistics Params:', params); // Debug log

    const response = await api.get("/sourcing_leads/sourcing_statistics", { params });
    return response.data;
  } catch (error) {
    console.error('Error in fetching sourcing statistics:', error);
    throw error;
  }
};

export const getLeadStatistics = async (limit = 100, offset = 0, from_date = null, to_date = null, source = null, role_group_id = null, cluster_id = null) => {
  try {
    const selectedRegion = localStorage.getItem('selectedRegion') || "1";
    const params = {
      limit,
      offset,
      from_date,
      to_date,
      source,
      region_id: selectedRegion,
      role_group_id: role_group_id ? parseInt(role_group_id) : null,
      cluster_id: cluster_id ? parseInt(cluster_id) : null
    };

    // Remove null values from params
    Object.keys(params).forEach(key => params[key] === null && delete params[key]);

    console.log('Lead Statistics Params:', params); // Debug log

    const response = await api.get("/sourcing_leads/statistics", { params });
    return {
      items: response.data?.items || [],
      total: response.data?.total || 0
    };
  } catch (error) {
    console.error('Error in fetching lead statistics:', error);
    throw error;
  }
};

// Get state transitions statistics
export const getStateTransitions = async (from_date = null, to_date = null, source = null, role_group_id = null, cluster_id = null) => {
  try {
    const region_id = localStorage.getItem('selectedRegion') || "1";
    const params = {
      from_date,
      to_date,
      source,
      region_id,
      role_group_id: role_group_id ? parseInt(role_group_id) : null,
      cluster_id: cluster_id ? parseInt(cluster_id) : null
    };

    // Remove null values from params
    Object.keys(params).forEach(key => params[key] === null && delete params[key]);

    const response = await api.get("/sourcing_leads/runner_state_statistics", { params });
    return response.data;
  } catch (error) {
    console.error('Error in fetching state transitions:', error);
    throw error;
  }
};

// Update the permission check to not use expiry time
export const checkPermission = (resource, action) => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  return permissions.some(permission => 
    permission.resource === resource && permission.action === action
  );
};

// Update user status
export const updateUserStatus = async (user_id, status) => {
  try {
    const response = await api.put(`/users/${user_id}`, {
      is_active: status,
    });
    return response;
  } catch (error) {
    console.error("Error in changing Status:", error);
    throw error;
  }
};

// Check if lead/ER already exists for a phone number
export const checkLeadERExists = async (phone) => {
  try {
    const response = await api.get("/sourcing_leads/check-lead-er-exists", {
      params: { phone },
    });
    return response.data;
  } catch (error) {
    console.error("Error checking lead/ER existence:", error);
    throw error;
  }
};

// resend er code
export const resendERCode = async (phone) => {
  try {
    await api.post(`/sourcing_leads/resend_er_code`, {
      phone: phone,
    });
    toast.success("Er code send successfully");
    return;
  } catch (error) {
    console.error("Error in sending er code:", error);
    throw error;
  }
};

// Update the setPermissions function to not use expiry time
export const setPermissions = (permissions) => {
  localStorage.setItem('permissions', JSON.stringify(permissions));
};

export const createBulkPotentialLeads = async (leads) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    const response = await api.post("/sourcing_leads/potential_lead/bulk_upload", {
      leads
    });
    return response.data;
  } catch (error) {
    if (error.response) {
  
      if (error.response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(error.response.data?.message || 'Failed to create bulk leads');
    } else if (error.request) {
      throw new Error('No response from server. Please try again.');
    } else {
      throw error;
    }
  }
};

export default api; 